import { ThemeEvents, CartAddEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { fetchConfig } from '@theme/utilities';

/** How long the "Added" / error label shows before the button resets (ms). */
const FEEDBACK_DURATION = 2000;

/**
 * `<size-alternatives>` — "Available in your size" suggestions on the PDP.
 *
 * All the rendering is server-side (blocks/size-alternatives.liquid). On a size change the
 * variant picker already fetches the full product page for the new variant; this element
 * just morphs its own markup from that response, so it costs no extra network requests.
 *
 * Each card has an "Add to Cart - [size]" button. Clicks are handled by one delegated
 * listener on this element, so it keeps working after the cards are morphed. Adding follows
 * the theme's own flow (complete-the-look.js / product-form.js): POST /cart/add.js asking for
 * the cart sections, then dispatch CartAddEvent so the cart drawer opens and re-renders.
 */
class SizeAlternatives extends HTMLElement {
  /** @type {Element | null} */
  #scope = null;

  connectedCallback() {
    this.#scope = this.closest('.shopify-section') ?? document.body;
    this.#scope.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.#scope?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.removeEventListener('click', this.#onClick);
    this.#scope = null;
  }

  /**
   * @param {Event & { detail?: { data?: { html?: Document, productId?: string, newProduct?: { id: string } } } }} event
   */
  #onVariantUpdate = (event) => {
    const data = event.detail?.data;
    if (!data?.html) return;

    // Ignore updates from other products' pickers (e.g. quick-add cards in this section).
    if (data.newProduct) {
      this.dataset.productId = data.newProduct.id;
    } else if (data.productId && data.productId !== this.dataset.productId) {
      return;
    }

    const next = data.html.getElementById(this.id);
    if (!next) return;

    morph(this, next);
    // The theme's morph() defaults to childrenOnly, so it never touches this element's own
    // attributes. Sync [hidden] by hand: it shows/hides the block and drives the CSS that
    // swaps out Complete the Look (see blocks/size-alternatives.liquid).
    this.hidden = next.hasAttribute('hidden');
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-add-variant-id]') : null;
    if (button instanceof HTMLButtonElement) this.#addToCart(button);
  };

  /** @param {HTMLButtonElement} button */
  async #addToCart(button) {
    if (button.getAttribute('aria-busy') === 'true') return;

    const variantId = Number(button.dataset.addVariantId);
    const label = button.querySelector('.size-alternatives__add-text');
    const defaultText = label?.textContent ?? '';

    button.setAttribute('aria-busy', 'true');

    // Mirror the theme: ask for the cart sections so the drawer/page can morph.
    const sections = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    const body = JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sections.join(',') });

    let feedback = this.dataset.errorText || 'Error adding to cart';
    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body })).then((res) =>
        res.json()
      );

      if (response.status) {
        console.error('Size alternatives: failed to add to cart', response);
        if (response.description) feedback = response.description;
      } else {
        feedback = Theme.translations?.added || 'Added';
        // cart-icon.js treats itemCount as the new cart TOTAL for non-product-form sources,
        // and /cart/add.js doesn't return it, so read it from /cart.js (click-only request).
        const itemCount = await fetch(`${Theme.routes.cart_url}.js`)
          .then((res) => res.json())
          .then((cart) => cart.item_count)
          .catch(() => undefined);
        this.dispatchEvent(
          new CartAddEvent({}, String(variantId), {
            source: 'size-alternatives',
            itemCount,
            productId: button.dataset.productId,
            variantId: String(variantId),
            sections: response.sections,
          })
        );
      }
    } catch (error) {
      console.error('Size alternatives: error adding to cart', error);
    }

    // Show the result on the button and announce it, then reset.
    if (label) label.textContent = feedback;
    this.#announce(feedback);
    setTimeout(() => {
      if (label && label.isConnected) label.textContent = defaultText;
      button.removeAttribute('aria-busy');
    }, FEEDBACK_DURATION);
  }

  /** @param {string} message */
  #announce(message) {
    const region = this.querySelector('[aria-live]');
    if (region) region.textContent = message;
  }
}

if (!customElements.get('size-alternatives')) {
  customElements.define('size-alternatives', SizeAlternatives);
}
